import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { api, useAuth } from '../contexts/AuthContext';

const Settings = () => {
  const { user } = useAuth();
  const [org, setOrg] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [logoFile, setLogoFile] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [savingOrg, setSavingOrg] = useState(false);

  const canEditOrg = useMemo(() => user?.role === 'admin' || user?.role === 'superadmin', [user]);

  useEffect(() => {
    let isMounted = true;
    const load = async () => {
      try {
        const res = await api.get('/org');
        if (!isMounted) return;
        setOrg(res.data);
        setCompanyName(res.data.company_name || res.data.name || '');
      } catch (e) {
        toast.error(e?.response?.data?.error || 'Erro ao carregar empresa');
      }
      try {
        const logoRes = await api.get('/org/logo', { responseType: 'blob' });
        if (!isMounted) return;
        const blob = logoRes.data;
        if (!blob || (blob.size !== undefined && blob.size === 0)) {
          setLogoUrl('');
          return;
        }
        const url = URL.createObjectURL(blob);
        setLogoUrl(url);
      } catch {
        if (isMounted) setLogoUrl('');
      }
    };
    load();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (logoUrl) URL.revokeObjectURL(logoUrl);
    };
  }, [logoUrl]);

  const saveOrg = async (e) => {
    e.preventDefault();
    if (!canEditOrg) return;
    setSavingOrg(true);
    try {
      const res = await api.put('/org', { company_name: companyName });
      setOrg(res.data);
      toast.success('Empresa atualizada');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao salvar empresa');
    } finally {
      setSavingOrg(false);
    }
  };

  const uploadLogo = async (e) => {
    e.preventDefault();
    if (!canEditOrg) return;
    if (!logoFile) return toast.error('Selecione um arquivo PNG/JPG');
    setSavingOrg(true);
    try {
      const fd = new FormData();
      fd.append('logo', logoFile);
      const res = await api.post('/org/logo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setOrg(res.data);
      setLogoFile(null);
      toast.success('Logo atualizado');
      try {
        const logoRes = await api.get('/org/logo', { responseType: 'blob' });
        const url = URL.createObjectURL(logoRes.data);
        if (logoUrl) URL.revokeObjectURL(logoUrl);
        setLogoUrl(url);
      } catch {}
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao enviar logo');
    } finally {
      setSavingOrg(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Empresa</h1>
      </div>

      <div className="card">
        <div className="card-header">
          <h2 className="text-lg font-semibold text-gray-900">Empresa</h2>
          <p className="text-sm text-gray-600">Nome e logo usados no relatório PDF</p>
        </div>
        <div className="card-body space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-28 h-16 border rounded bg-white flex items-center justify-center overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt="Logo" className="max-w-full max-h-full" />
              ) : (
                <span className="text-xs text-gray-500">Sem logo</span>
              )}
            </div>
            <div className="flex-1">
              <div className="text-sm text-gray-700">{org?.company_name || org?.name || '—'}</div>
              <div className="text-xs text-gray-500">ID: {org?.id || '—'}</div>
            </div>
          </div>

          <form onSubmit={saveOrg} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nome da empresa</label>
              <input
                className="mt-1 input"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                disabled={!canEditOrg || savingOrg}
                placeholder="Ex: Drone Consultoria Ltda"
              />
            </div>
            <button className="btn btn-primary" disabled={!canEditOrg || savingOrg} type="submit">
              Salvar empresa
            </button>
            {!canEditOrg && (
              <div className="text-xs text-gray-500">Somente administradores podem alterar o nome e o logo.</div>
            )}
          </form>

          <form onSubmit={uploadLogo} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Logo (PNG/JPG)</label>
              <input
                type="file"
                accept="image/png,image/jpeg"
                className="mt-1 block w-full text-sm text-gray-700"
                onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                disabled={!canEditOrg || savingOrg}
              />
            </div>
            <button className="btn btn-secondary" disabled={!canEditOrg || savingOrg} type="submit">
              Enviar logo
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Settings;
